// src/components/tickets/QRGenerator.js
import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const RELAYER_URL = process.env.REACT_APP_RELAYER_BASE_URL ||
                    process.env.REACT_APP_SIGNATURE_API ||
                    process.env.REACT_APP_RELAYER_URL ||
                    'http://localhost:3002';

// Helper to format checkin time
const formatCheckinTime = (timestamp) => {
  if (!timestamp || timestamp === 0) return null;
  const date = new Date(timestamp * 1000);
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function QRGenerator({ ticket, action, ticketState, holderAddress }) {
  const [qrData, setQrData] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if ticket is already used/checked-in
  const isCheckedIn = ticket?.used || ticketState?.used;
  const checkinTime = ticket?.checkinTime || ticketState?.checkinTime;

  useEffect(() => {
    // Don't generate QR for checked-in tickets
    if (isCheckedIn) return;
    if (!ticket?.tokenId || !action) return;

    let intervalId = null;
    let currentData = null;

    // Function to generate fresh QR data with server signature
    const generateQRData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Request signature from server
        const response = await fetch(`${RELAYER_URL}/api/relayer/generate-signature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tokenId: ticket.tokenId,
            action: action
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();

        // Server returns: { tokenId, action, expiry, nonce, signature }
        // Add holderAddress for ownership verification at scanner
        const qrDataWithHolder = {
          ...data,
          holderAddress: holderAddress || null,
        };

        console.log('[QRGenerator] Generated signed QR code:', {
          tokenId: qrDataWithHolder.tokenId,
          action: qrDataWithHolder.action,
          expiry: qrDataWithHolder.expiry,
          hasSignature: !!qrDataWithHolder.signature,
          holderAddress: qrDataWithHolder.holderAddress,
        });

        setQrData(qrDataWithHolder);
        setSecondsLeft(60); // Server sets 60 second expiry
        setLoading(false);
        return data;
      } catch (err) {
        console.error('[QRGenerator] Failed to generate signature:', err);
        setError(err.message);
        setLoading(false);

        // Fallback: Generate unsigned QR code (won't work with offline scanner)
        const fallbackData = {
          tokenId: ticket.tokenId,
          ticketTypeId: ticket.ticketTypeId,
          serialNumber: ticket.serialNumber,
          action: action,
          timestamp: Math.floor(Date.now() / 1000),
          expiry: Math.floor(Date.now() / 1000) + 60,
          holderAddress: holderAddress || null,
          unsigned: true, // Mark as unsigned
          error: 'Server unavailable - offline validation disabled'
        };
        setQrData(fallbackData);
        setSecondsLeft(60);
        return fallbackData;
      }
    };

    // Initialize - generate first QR code
    const initialize = async () => {
      currentData = await generateQRData();

      // Start countdown timer
      intervalId = setInterval(() => {
        if (!currentData) return;

        const now = Math.floor(Date.now() / 1000);
        const remaining = Math.max(0, currentData.expiry - now);
        setSecondsLeft(remaining);

        // Refresh QR code when it expires
        if (remaining === 0) {
          generateQRData().then(newData => {
            currentData = newData;
          });
        }
      }, 1000);
    };

    initialize();

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [ticket?.tokenId, action, ticket?.ticketTypeId, ticket?.serialNumber, isCheckedIn, holderAddress]);

  // Show checked-in message if ticket is already used
  if (isCheckedIn) {
    const formattedTime = formatCheckinTime(checkinTime);
    return (
      <div className="qr-container checked-in">
        <div className="checked-in-badge">
          <span className="checked-in-icon">✓</span>
          <span className="checked-in-text">Already Checked In</span>
        </div>
        {formattedTime && (
          <p className="checkin-time">
            Checked in at: {formattedTime}
          </p>
        )}
        <p className="checkin-note">
          This ticket has already been validated and used for entry.
        </p>
      </div>
    );
  }

  if (loading && !qrData) {
    return <div className="qr-placeholder">Generating secure QR code...</div>;
  }

  if (!qrData) {
    return <div className="qr-placeholder">Generating QR...</div>;
  }

  return (
    <div className="qr-container">
      <QRCodeSVG
        value={JSON.stringify(qrData)}
        size={256}
        level="H"
        includeMargin={true}
      />
      <div className="qr-info">
        <p className="expiry-notice">
          {qrData.unsigned ? '⚠️ ' : '🔒 '}
          Expires in {secondsLeft}s
        </p>
        {qrData.unsigned && (
          <p className="unsigned-warning">
            Offline validation unavailable
          </p>
        )}
        {error && !qrData.unsigned && (
          <p className="qr-error-text">
            Error: {error}
          </p>
        )}
      </div>
    </div>
  );
}
